import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  useObligation,
  useUpdateObligation,
  useChangeObligationStatus,
  useAddResponsible,
  useRemoveResponsible,
  useDirectory,
} from '../../../platform/api/hooks';
import { asRecord, str, asList } from '../../../platform/api/utils';
import { usePermissions } from '../../../platform/auth/permissions';
import { Badge } from '../../../components/ui/Badge';
import { Section } from '../../../components/ui/Section';

import {
  type EditForm,
  type Tab,
  TYPE_LABELS,
  STATUS_LABELS,
  STATUS_VARIANTS,
  STATUS_REQUIRES_REASON,
  editFormSchema,
  SkeletonDetail,
  primaryBtn,
  secondaryBtn,
  linkBtn,
  modalOverlay,
} from './obligation-detail.support';
import { ObligationDadosTab } from './obligation-detail.dados-tab';
import { ObligationResponsiblesTab } from './ObligationResponsiblesTab';

export function ObligationDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: obligation, isLoading, isError } = useObligation(id ?? '');
  const { can } = usePermissions();
  const updateObligation = useUpdateObligation();
  const changeStatus = useChangeObligationStatus();
  const addResponsible = useAddResponsible();
  const removeResponsible = useRemoveResponsible();

  const [editing, setEditing] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>('dados');
  const [statusChangeOpen, setStatusChangeOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusReason, setStatusReason] = useState('');
  const [addRespOpen, setAddRespOpen] = useState(false);
  const [responsiveSearch, setResponsibleSearch] = useState('');

  const o = asRecord(obligation);
  const st = str(o.status);
  const vis = str(o.visibility);
  const tp = str(o.type);

  const responsibles = (o.responsibles as Record<string, unknown>[] | undefined) ?? [];

  const { data: peopleData } = useDirectory(
    responsiveSearch ? { search: responsiveSearch, limit: 50 } : { limit: 50 },
  );
  const people = asList(peopleData);

  const form = useForm<EditForm>({
    resolver: zodResolver(editFormSchema),
    values: {
      title: str(o.title),
      type: tp,
      visibility: vis,
      description: str(o.description),
      expectedDate: typeof o.expectedDate === 'string' ? o.expectedDate.substring(0, 10) : '',
      completedDate: typeof o.completedDate === 'string' ? o.completedDate.substring(0, 10) : '',
      restrictedNotes: str(o.restrictedNotes),
      eventId: str(o.eventId),
      visibleInMemberApp: !!o.visibleInMemberApp,
      safeDisplayTitle: str(o.safeDisplayTitle),
      safeDisplaySummary: str(o.safeDisplaySummary),
    },
  });

  if (isLoading) return <SkeletonDetail />;
  if (isError || !id) {
    return (
      <div style={{ padding: '2.25rem 2.5rem', maxWidth: 900 }}>
        <Section title="Erro">
          <p style={{ color: 'var(--danger)', margin: 0 }}>
            Não foi possível carregar esta obrigação.
          </p>
          <button
            type="button"
            onClick={() => {
              void navigate('/obrigacoes');
            }}
            style={linkBtn}
          >
            ← Voltar para obrigações
          </button>
        </Section>
      </div>
    );
  }

  function handleSave(data: EditForm) {
    if (!id) return;
    const { restrictedNotes, ...rest } = data;
    updateObligation.mutate({
      id,
      data: {
        ...rest,
        ...(can('obligations.restricted.read') ? { restrictedNotes } : {}),
        expectedDate: data.expectedDate ? new Date(data.expectedDate).toISOString() : undefined,
        completedDate: data.completedDate ? new Date(data.completedDate).toISOString() : undefined,
      },
    });
    setEditing(false);
  }

  function handleStatusChange() {
    if (!newStatus || !id) return;
    changeStatus.mutate({ id, status: newStatus });
    setStatusChangeOpen(false);
    setNewStatus('');
    setStatusReason('');
  }

  function handleAddResponsible(personId: string) {
    if (!id) return;
    addResponsible.mutate({ id, personId });
    setAddRespOpen(false);
  }

  function handleRemoveResponsible(responsibleId: string) {
    if (!id) return;
    removeResponsible.mutate({ id, responsibleId });
  }

  const needsReason = newStatus && STATUS_REQUIRES_REASON[newStatus];
  const disableConfirm = [
    newStatus === '',
    needsReason && !statusReason.trim(),
    changeStatus.isPending,
  ].includes(true);

  return (
    <div style={{ padding: '2.25rem 2.5rem', maxWidth: 900 }}>
      <button
        type="button"
        onClick={() => {
          void navigate('/obrigacoes');
        }}
        style={linkBtn}
      >
        ← Voltar para obrigações
      </button>

      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.25rem',
          marginTop: '0.5rem',
        }}
      >
        <div>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 700, margin: 0 }}>{str(o.title)}</h2>
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.35rem' }}>
            <Badge label={TYPE_LABELS[tp] ?? tp} variant="info" />
            <span style={{ color: 'var(--line)' }}>|</span>
            <span style={{ fontSize: '0.8rem', color: 'var(--muted)' }}>
              {TYPE_LABELS[tp] ?? tp}
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <Badge label={STATUS_LABELS[st] ?? st} variant={STATUS_VARIANTS[st] ?? 'default'} />
          {!editing && can('obligations.update') && (
            <button
              type="button"
              onClick={() => {
                setEditing(true);
              }}
              style={secondaryBtn}
            >
              Editar
            </button>
          )}
          {!editing && can('obligations.status.change') && (
            <button
              type="button"
              onClick={() => {
                setStatusChangeOpen(true);
              }}
              style={{
                ...secondaryBtn,
                background: 'var(--warning-soft)',
                borderColor: 'var(--warning)',
                color: 'var(--warning)',
              }}
            >
              Alterar status
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div
        style={{
          display: 'flex',
          gap: 0,
          marginBottom: '1rem',
          borderBottom: '2px solid var(--line)',
        }}
      >
        {(
          [
            ['dados', 'Dados'],
            ['responsaveis', `Responsáveis (${String(responsibles.length)})`],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => {
              setActiveTab(key);
            }}
            style={{
              padding: '0.5rem 1rem',
              fontSize: '0.85rem',
              fontWeight: activeTab === key ? 600 : 400,
              color: activeTab === key ? 'var(--green)' : 'var(--muted)',
              border: 'none',
              borderBottom: activeTab === key ? '2px solid var(--green)' : '2px solid transparent',
              background: 'transparent',
              cursor: 'pointer',
              marginBottom: -2,
            }}
          >
            {label}
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {/* Modal: Alterar status */}
        {statusChangeOpen && (
          <div
            style={modalOverlay}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setStatusChangeOpen(false);
              }
            }}
          >
            <div
              style={{
                background: '#fff',
                borderRadius: 14,
                padding: '1.5rem',
                minWidth: 360,
                maxWidth: 420,
              }}
            >
              <h3 style={{ margin: '0 0 1rem', fontSize: '1rem' }}>Alterar status</h3>
              <select
                value={newStatus}
                onChange={(e) => {
                  setNewStatus(e.target.value);
                }}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid var(--line)',
                  borderRadius: 10,
                  fontSize: '0.875rem',
                  marginBottom: '0.75rem',
                  boxSizing: 'border-box',
                }}
              >
                <option value="">Selecione...</option>
                {Object.entries(STATUS_LABELS).map(([v, l]) => (
                  <option key={v} value={v} disabled={v === st}>
                    {l}
                  </option>
                ))}
              </select>
              {needsReason && (
                <textarea
                  placeholder="Motivo da alteração (obrigatório)"
                  value={statusReason}
                  onChange={(e) => {
                    setStatusReason(e.target.value);
                  }}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid var(--line)',
                    borderRadius: 10,
                    fontSize: '0.85rem',
                    marginBottom: '0.75rem',
                    minHeight: 60,
                    resize: 'vertical',
                    boxSizing: 'border-box',
                  }}
                />
              )}
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => {
                    setStatusChangeOpen(false);
                  }}
                  style={secondaryBtn}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleStatusChange}
                  disabled={disableConfirm}
                  style={primaryBtn}
                >
                  {changeStatus.isPending ? 'Salvando...' : 'Confirmar'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tab: Dados */}
        {activeTab === 'dados' && (
          <ObligationDadosTab
            editing={editing}
            form={form}
            o={o}
            tp={tp}
            st={st}
            vis={vis}
            updatePending={updateObligation.isPending}
            onCancelEdit={() => {
              setEditing(false);
            }}
            onSave={handleSave}
          />
        )}
        {activeTab === 'responsaveis' && (
          <ObligationResponsiblesTab
            responsibles={responsibles}
            people={people}
            addOpen={addRespOpen}
            search={responsiveSearch}
            onOpenAdd={() => {
              setAddRespOpen(true);
            }}
            onClose={() => {
              setAddRespOpen(false);
              setResponsibleSearch('');
            }}
            onSearchChange={setResponsibleSearch}
            onAdd={handleAddResponsible}
            onRemove={handleRemoveResponsible}
            canManage={can('obligations.responsibles.manage')}
            removing={removeResponsible.isPending}
          />
        )}
      </div>
    </div>
  );
}
